import { createContext, useContext, useState, useCallback } from 'react';

// Event types for data changes
export const DATA_EVENTS = {
  CANDIDATE_CREATED: 'candidate_created',
  CANDIDATE_UPDATED: 'candidate_updated',
  CANDIDATE_DELETED: 'candidate_deleted',
  JOB_CREATED: 'job_created',
  JOB_UPDATED: 'job_updated',
  JOB_DELETED: 'job_deleted',
  MATCH_UPDATED: 'match_updated',
  COMPANY_UPDATED: 'company_updated',
};

const DataRefreshContext = createContext(null);

export function DataRefreshProvider({ children }) {
  const [refreshTriggers, setRefreshTriggers] = useState({});
  const [listeners, setListeners] = useState({});

  // Trigger a refresh for specific data types
  const triggerRefresh = useCallback((eventType, data = {}) => {
    const timestamp = Date.now();
    setRefreshTriggers(prev => ({
      ...prev,
      [eventType]: { timestamp, data }
    }));

    // Also trigger related refreshes
    // When candidate/job changes, dashboard stats should refresh
    if (eventType.includes('candidate') || eventType.includes('job') || eventType.includes('match')) {
      setRefreshTriggers(prev => ({
        ...prev,
        dashboard: { timestamp, data }
      }));
    }

    // Notify all listeners
    if (listeners[eventType]) {
      listeners[eventType].forEach(callback => callback(data));
    }
  }, [listeners]);

  // Subscribe to refresh events
  const subscribe = useCallback((eventType, callback) => {
    setListeners(prev => ({
      ...prev,
      [eventType]: [...(prev[eventType] || []), callback]
    }));

    // Return unsubscribe function
    return () => {
      setListeners(prev => ({
        ...prev,
        [eventType]: (prev[eventType] || []).filter(cb => cb !== callback)
      }));
    };
  }, []);

  // Get refresh trigger for a specific type
  const getRefreshTrigger = useCallback((eventType) => {
    return refreshTriggers[eventType]?.timestamp || 0;
  }, [refreshTriggers]);

  return (
    <DataRefreshContext.Provider value={{ triggerRefresh, subscribe, getRefreshTrigger, refreshTriggers }}>
      {children}
    </DataRefreshContext.Provider>
  );
}

export function useDataRefresh() {
  const context = useContext(DataRefreshContext);
  if (!context) {
    throw new Error('useDataRefresh must be used within a DataRefreshProvider');
  }
  return context;
}

// Custom hook for auto-refreshing data
export function useAutoRefresh(fetchFunction, dependencies = [], eventTypes = []) {
  const { refreshTriggers } = useDataRefresh();
  
  // Create a combined trigger from all event types
  const combinedTrigger = eventTypes.reduce((acc, type) => {
    return acc + (refreshTriggers[type]?.timestamp || 0);
  }, 0);

  return [...dependencies, combinedTrigger];
}
